// Helpers for the editor estimate (payload building + Spanish messages), kept pure so they are unit-testable.
// No counting or ingestion math lives here: the engine returns everything, the front only plays it back.

import { formatDateTime24 } from '../../utils/formatTime24';

const plural = (n, singular, pluralForm) => `${n} ${n === 1 ? singular : pluralForm}`;

const formatNumber = (n) => {
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',');
};

export const formatMinutes = (minutes) => {
  if (minutes < 60) return plural(Math.round(minutes), 'minuto', 'minutos');
  if (minutes < 1440) return plural(Math.round(minutes / 60), 'hora', 'horas');
  return plural(Math.round(minutes / 1440), 'día', 'días');
};

// Real time shown per second of video, e.g. 86400 -> "1 día".
export const formatSpeedFactor = (seconds) => {
  if (seconds < 60) return plural(Math.round(seconds), 'segundo', 'segundos');
  if (seconds < 3600) return plural(Math.round(seconds / 60), 'minuto', 'minutos');
  if (seconds < 86400) {
    const hours = seconds / 3600;
    return `${formatNumber(hours)} ${hours === 1 ? 'hora' : 'horas'}`;
  }
  const days = seconds / 86400;
  return `${formatNumber(days)} ${days === 1 ? 'día' : 'días'}`;
};

// Same instants the creation request submits: new Date(value).toISOString(), sent as UTC wall-clock (no 'Z')
// together with timeZone 'UTC' (creation sends no zone, which the backend defaults to UTC).
const toUtcLocalDateTime = (value) => new Date(value).toISOString().slice(0, 19);

// Payload consistent with the real creation request so the estimate matches the render. Null when not computable.
export const buildEstimatePayload = (config, feedId) => {
  if (!feedId) return null;
  const payload = {
    feedId,
    period: config.period,
    timeZone: 'UTC',
    intervalMinutes: Number(config.intervalMinutes),
    durationSeconds: Number(config.durationSeconds),
  };

  if (config.period === 'CUSTOM') {
    if (!config.customStart || !config.customEnd) return null;
    const start = new Date(config.customStart);
    const end = new Date(config.customEnd);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null;
    payload.customStart = toUtcLocalDateTime(config.customStart);
    payload.customEnd = toUtcLocalDateTime(config.customEnd);
  }

  return payload;
};

// Index-based playback: progress p in [0,1] -> photo index in [0, n-1] (-1 when there are no photos).
export const frameIndexAtProgress = (progress, n) => {
  if (!(n > 0)) return -1;
  return Math.min(n - 1, Math.floor(progress * n));
};

// Exact photo instant shown in the browser time zone (or the given one) with date and time.
export const formatPhotoClock = (isoInstant, timeZone) =>
  formatDateTime24(isoInstant, { timeZone, dateOptions: { day: 'numeric', month: 'short', year: 'numeric' } });

// Returns { level: 'ok' | 'info' | 'warning' | 'error', message }.
export const describeEstimate = (estimate, requestedDurationSeconds) => {
  if (estimate.status === 'NO_FRAMES') {
    return {
      level: 'error',
      message:
        'No hay fotos de esta cámara en el período elegido, por lo que no se podría armar el video. Elegí otro período.',
    };
  }

  if (estimate.status === 'ADJUSTED' && estimate.reason !== 'SHORTER_THAN_REQUESTED') {
    const suggestion = estimate.suggestedIntervalMinutes
      ? ` Para evitar el ajuste, probá con un intervalo de ${formatMinutes(estimate.suggestedIntervalMinutes)}.`
      : '';
    return {
      level: 'warning',
      message:
        `Hay demasiadas fotos (${estimate.requestedFrames}) para ${requestedDurationSeconds} segundos de video. ` +
        `Para respetar el período y la duración elegidos, el video usará aproximadamente 1 foto cada ${formatMinutes(estimate.effectiveIntervalMinutes)}.` +
        suggestion,
    };
  }

  if (estimate.reason === 'SHORTER_THAN_REQUESTED') {
    return {
      level: 'info',
      message:
        `Con este período e intervalo hay pocas fotos (${estimate.usedFrames}): el video durará ${formatSpeedFactor(estimate.actualDurationSeconds)} ` +
        `en lugar de ${requestedDurationSeconds} segundos. Esto no es un error.`,
    };
  }

  return {
    level: 'ok',
    message: `Todo en orden: el video usará ${estimate.usedFrames} fotos a ${estimate.fps} fps y durará ${formatSpeedFactor(estimate.actualDurationSeconds)}.`,
  };
};
